/**
 * Countries, as codes. The product keys jurisdiction, business hours and the
 * audience filters on ISO-3166 alpha-2, and until the audience picker nothing
 * ever needed a country's NAME — the browser supplies those through
 * `Intl.DisplayNames`, so this file carries no name table for display. What it
 * carries is the list itself (so a typed code can be checked rather than
 * shape-matched), the aliases people actually type, the regions a marketer
 * thinks in, and the one name→code table both connectors used to keep apart.
 */

/**
 * ISO-3166-1 alpha-2, plus `XK` (Kosovo): user-assigned, not ISO, but what
 * HubSpot, both people-data vendors and `Intl.DisplayNames` all use.
 */
export const COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ',
  'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ',
  'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ',
  'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET',
  'FI', 'FJ', 'FK', 'FM', 'FO', 'FR',
  'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY',
  'HK', 'HM', 'HN', 'HR', 'HT', 'HU',
  'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT',
  'JE', 'JM', 'JO', 'JP',
  'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ',
  'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY',
  'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ',
  'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ',
  'OM',
  'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY',
  'QA',
  'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ',
  'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ',
  'UA', 'UG', 'UM', 'US', 'UY', 'UZ',
  'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU',
  'WF', 'WS',
  'XK',
  'YE', 'YT',
  'ZA', 'ZM', 'ZW',
] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number];

const CODE_SET: ReadonlySet<string> = new Set(COUNTRY_CODES);

/**
 * What people type that is not the ISO code. `UK` is the one that matters —
 * it was typed into audiences while the filter accepted any two letters — and
 * `EL` is Greece in every EU statistical table.
 */
export const COUNTRY_ALIASES: Readonly<Record<string, CountryCode>> = { UK: 'GB', EL: 'GR' };

export function isCountryCode(value: string): value is CountryCode {
  return CODE_SET.has(value);
}

/** A typed code to the ISO one, or null when it names no country. */
export function normaliseCountryCode(value: string | null | undefined): CountryCode | null {
  if (!value) return null;
  const upper = value.trim().toUpperCase();
  const code = COUNTRY_ALIASES[upper] ?? upper;
  return isCountryCode(code) ? code : null;
}

export const EU = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
] as const satisfies readonly CountryCode[];

/**
 * §7.2's GDPR/ePrivacy jurisdictions: the EU, the three EEA states and the
 * UK. The seed writes `suppression_country_rules` from this.
 */
export const EEA_PLUS_UK = [...EU, 'IS', 'LI', 'NO', 'GB'] as const satisfies readonly CountryCode[];

export interface Region {
  key: string;
  label: string;
  codes: readonly CountryCode[];
}

/** The groups a marketer picks by name rather than country by country. */
export const REGIONS: readonly Region[] = [
  { key: 'eu', label: 'European Union', codes: EU },
  { key: 'eea_uk', label: 'EEA + UK', codes: EEA_PLUS_UK },
  { key: 'dach', label: 'DACH', codes: ['DE', 'AT', 'CH'] },
  { key: 'nordics', label: 'Nordics', codes: ['DK', 'FI', 'IS', 'NO', 'SE'] },
  { key: 'benelux', label: 'Benelux', codes: ['BE', 'NL', 'LU'] },
  { key: 'uk_ie', label: 'UK & Ireland', codes: ['GB', 'IE'] },
  { key: 'north_america', label: 'North America', codes: ['US', 'CA', 'MX'] },
  { key: 'latam', label: 'Latin America', codes: ['AR', 'BR', 'CL', 'CO', 'MX', 'PE', 'UY'] },
  { key: 'middle_east', label: 'Middle East', codes: ['AE', 'BH', 'EG', 'IL', 'JO', 'KW', 'OM', 'QA', 'SA', 'TR'] },
  { key: 'apac', label: 'Asia-Pacific', codes: ['AU', 'CN', 'HK', 'ID', 'IN', 'JP', 'KR', 'MY', 'NZ', 'PH', 'SG', 'TH', 'TW', 'VN'] },
];

/**
 * Names to codes, for what a CRM or a vendor sends. HubSpot stores country as
 * free text, so "United States", "USA" and "US" all occur in one portal;
 * Amplemarket returns a name. Gate 8 and the §7.2 matrix key on a code, and
 * a mismatch there is the wrong jurisdiction's rules applied to a person —
 * so an unrecognised name returns null, which §7.2 treats as FLAGGED, the
 * strictest reading. Wrong in the safe direction; never a guess.
 */
const NAMES: Readonly<Record<string, CountryCode>> = {
  'united states': 'US',
  'united states of america': 'US',
  usa: 'US',
  'u.s.': 'US',
  'u.s.a.': 'US',
  america: 'US',
  canada: 'CA',
  'united kingdom': 'GB',
  uk: 'GB',
  'great britain': 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  ireland: 'IE',
  israel: 'IL',
  germany: 'DE',
  deutschland: 'DE',
  france: 'FR',
  spain: 'ES',
  españa: 'ES',
  italy: 'IT',
  italia: 'IT',
  netherlands: 'NL',
  'the netherlands': 'NL',
  holland: 'NL',
  belgium: 'BE',
  switzerland: 'CH',
  austria: 'AT',
  sweden: 'SE',
  norway: 'NO',
  denmark: 'DK',
  finland: 'FI',
  poland: 'PL',
  portugal: 'PT',
  australia: 'AU',
  'new zealand': 'NZ',
  japan: 'JP',
  china: 'CN',
  india: 'IN',
  singapore: 'SG',
  brazil: 'BR',
  mexico: 'MX',
  'south africa': 'ZA',
  'united arab emirates': 'AE',
  uae: 'AE',
  greece: 'GR',
  'czech republic': 'CZ',
  czechia: 'CZ',
  romania: 'RO',
  hungary: 'HU',
  turkey: 'TR',
  türkiye: 'TR',
  'south korea': 'KR',
  'hong kong': 'HK',
  'saudi arabia': 'SA',
  luxembourg: 'LU',
  estonia: 'EE',
  latvia: 'LV',
  lithuania: 'LT',
  bulgaria: 'BG',
  croatia: 'HR',
  slovakia: 'SK',
  slovenia: 'SI',
  cyprus: 'CY',
  malta: 'MT',
  iceland: 'IS',
  argentina: 'AR',
  chile: 'CL',
  colombia: 'CO',
};

/** A country as a CRM or a vendor wrote it — a code or a name — to its code, or null. */
export function countryCodeFromName(value: string | null | undefined): CountryCode | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^[A-Za-z]{2}$/.test(trimmed)) return normaliseCountryCode(trimmed);
  return NAMES[trimmed.toLowerCase()] ?? null;
}
